/**
 * newSurfacesTranslations.js — Feb 2026
 *
 * Shared translation store for strings on the Feb-2026 surfaces that were
 * originally hardcoded in English. Consumed by:
 *   • HeroBento (Succession CTA)
 *   • QuickZakatCalculator (embedded hero widget labels + result strip)
 *   • InheritanceLiveSidebar (right-hand live-results panel)
 *   • KnowledgePosters (grid + modal + mobile action bar)
 *
 * Translations are added by `/app/scripts/translate_new_surfaces.py` via
 * Claude Sonnet 4.5 through the Emergent LLM key. Missing languages fall
 * back to English automatically inside `tSurf()`.
 */
export const newSurfacesTranslations = {
  en: {
    hero: {
      ctaSuccession: 'Succession Planning',
    },
    quickCalc: {
      title: 'Quick Zakat check',
      cash: 'Cash & savings',
      debts: 'Short-term debts',
      gold: 'Gold (24K)',
      silver: 'Silver (999)',
      nisabBasis: 'Nisab basis',
      nisabSilver: 'Silver (595g)',
      nisabGold: 'Gold (85g)',
      status: 'Zakat status',
      dueAt: 'Zakat due at {rate}%',
      belowNisab: 'Below Nisab — no Zakat owed',
      nisabLabel: 'Nisab',
      netZakatable: 'Net zakatable',
      assets: 'Assets',
      debtsShort: 'Debts',
      privacy: 'Stays on your device. No account.',
      openFull: 'See full breakdown',
      live24k: 'Live 24K',
    },
    sidebar: {
      distributable: 'Distributable estate',
      gross: 'Gross',
      deductions: 'Deductions',
      progress: 'Progress',
      heirShares: 'Heir shares',
      stepEstate: 'Estate',
      stepDeductions: 'Deductions',
      stepHeirs: 'Heirs',
      stepResult: 'Result',
      jumpToResult: 'Jump to result',
      downloadCert: 'Download certificate',
      enterFirst: 'Enter estate + heirs to see shares.',
      pickHeirs: 'Pick heirs in Step 3 to preview shares.',
      remainder: 'Remainder',
      privacy: 'Everything you type on the left updates this panel instantly. Nothing leaves your device.',
    },
    posters: {
      kicker: 'Knowledge Posters',
      title: 'Islamic wealth wisdom, one poster at a time.',
      subtitle: 'Bite-sized, scholar-cited teachings on Zakah, inheritance and ethical wealth — free to download and share on WhatsApp, in your masjid, or with family.',
      readFull: 'Read full poster',
      comingSoon: 'Coming soon',
      dlPng: 'Download PNG (high-res)',
      dlPngBusy: 'Saving PNG…',
      dlPdf: 'Download PDF',
      dlPdfBusy: 'Building PDF…',
      share: 'Share',
      shareWa: 'Share on WhatsApp',
      mobileSave: 'Save to phone',
      mobileWa: 'WhatsApp',
      close: 'Close poster viewer',
      attribution: 'Content is excerpted directly from the cited scholarly works. Amanah does not modify or re-interpret the source material — posters are presented for education and community sharing.',
    },
  },
  ar: {
      hero: {
        ctaSuccession: 'تخطيط الميراث',
      },
      quickCalc: {
        title: 'فحص Zakat السريع',
        cash: 'النقد والمدخرات',
        debts: 'الديون قصيرة الأجل',
        gold: 'الذهب (عيار 24)',
        silver: 'الفضة (999)',
        nisabBasis: 'أساس Nisab',
        nisabSilver: 'الفضة (595 غرام)',
        nisabGold: 'الذهب (85 غرام)',
        status: 'حالة Zakat',
        dueAt: 'Zakah المستحقة بنسبة {rate}%',
        belowNisab: 'أقل من Nisab — لا Zakat مستحقة',
        nisabLabel: 'Nisab',
        netZakatable: 'صافي الخاضع للزكاة',
        assets: 'الأصول',
        debtsShort: 'الديون',
        privacy: 'يبقى على جهازك. لا حاجة لحساب.',
        openFull: 'عرض التفصيل الكامل',
        live24k: 'مباشر عيار 24',
      },
      sidebar: {
        distributable: 'التركة القابلة للتوزيع',
        gross: 'الإجمالي',
        deductions: 'الخصومات',
        progress: 'التقدم',
        heirShares: 'أنصبة الورثة',
        stepEstate: 'التركة',
        stepDeductions: 'الخصومات',
        stepHeirs: 'الورثة',
        stepResult: 'النتيجة',
        jumpToResult: 'الانتقال إلى النتيجة',
        downloadCert: 'تحميل الشهادة',
        enterFirst: 'أدخل التركة والورثة لمشاهدة الأنصبة.',
        pickHeirs: 'اختر الورثة في الخطوة 3 لمعاينة الأنصبة.',
        remainder: 'الباقي',
        privacy: 'كل ما تكتبه على اليسار يحدّث هذه اللوحة فوراً. لا شيء يغادر جهازك.',
      },
      posters: {
        kicker: 'ملصقات المعرفة',
        title: 'حكمة الثروة الإسلامية، ملصق تلو الآخر.',
        subtitle: 'تعاليم موجزة مدعومة بمراجع علمائية حول Zakah والميراث والثروة الأخلاقية — مجانية للتحميل والمشاركة عبر واتساب أو في المسجد أو مع العائلة.',
        readFull: 'قراءة الملصق كاملاً',
        comingSoon: 'قريباً',
        dlPng: 'تحميل PNG (دقة عالية)',
        dlPngBusy: 'جارٍ حفظ PNG…',
        dlPdf: 'تحميل PDF',
        dlPdfBusy: 'جارٍ إنشاء PDF…',
        share: 'مشاركة',
        shareWa: 'مشاركة عبر واتساب',
        mobileSave: 'حفظ على الهاتف',
        mobileWa: 'واتساب',
        close: 'إغلاق عارض الملصقات',
        attribution: 'المحتوى مقتبس مباشرة من الأعمال العلمية المذكورة. Amanah لا تُعدّل أو تُعيد تفسير المصدر الأصلي — الملصقات معروضة للتعليم والمشاركة المجتمعية.',
      },
  },

  ur: {
      hero: {
        ctaSuccession: 'جانشینی کی منصوبہ بندی',
      },
      quickCalc: {
        title: 'فوری زکوٰۃ کی جانچ',
        cash: 'نقدی اور بچت',
        debts: 'قلیل مدتی قرضے',
        gold: 'سونا (24K)',
        silver: 'چاندی (999)',
        nisabBasis: 'نصاب کی بنیاد',
        nisabSilver: 'چاندی (595g)',
        nisabGold: 'سونا (85g)',
        status: 'زکوٰۃ کی حیثیت',
        dueAt: 'زکوٰۃ واجب {rate}% پر',
        belowNisab: 'نصاب سے کم — کوئی زکوٰۃ واجب نہیں',
        nisabLabel: 'نصاب',
        netZakatable: 'خالص زکوٰۃ کے قابل',
        assets: 'اثاثے',
        debtsShort: 'قرضے',
        privacy: 'آپ کے آلے پر محفوظ۔ کوئی اکاؤنٹ نہیں۔',
        openFull: 'مکمل تفصیل دیکھیں',
        live24k: 'براہِ راست 24K',
      },
      sidebar: {
        distributable: 'قابلِ تقسیم ترکہ',
        gross: 'مجموعی',
        deductions: 'کٹوتیاں',
        progress: 'پیش رفت',
        heirShares: 'وارثوں کے حصے',
        stepEstate: 'ترکہ',
        stepDeductions: 'کٹوتیاں',
        stepHeirs: 'وارث',
        stepResult: 'نتیجہ',
        jumpToResult: 'نتیجے پر جائیں',
        downloadCert: 'سرٹیفکیٹ ڈاؤن لوڈ کریں',
        enterFirst: 'حصے دیکھنے کے لیے ترکہ اور وارث درج کریں۔',
        pickHeirs: 'حصوں کا پیش منظر دیکھنے کے لیے مرحلہ 3 میں وارث منتخب کریں۔',
        remainder: 'بقیہ',
        privacy: 'آپ بائیں جانب جو کچھ ٹائپ کرتے ہیں یہ پینل فوری طور پر اپ ڈیٹ ہو جاتا ہے۔ کوئی چیز آپ کے آلے سے باہر نہیں جاتی۔',
      },
      posters: {
        kicker: 'علمی پوسٹرز',
        title: 'اسلامی دولت کی حکمت، ایک وقت میں ایک پوسٹر۔',
        subtitle: 'Zakah، وراثت اور اخلاقی دولت پر علماء کے حوالہ جات کے ساتھ مختصر تعلیمات — واٹس ایپ، آپ کی مسجد، یا خاندان کے ساتھ ڈاؤن لوڈ اور شیئر کرنے کے لیے مفت۔',
        readFull: 'مکمل پوسٹر پڑھیں',
        comingSoon: 'جلد آ رہا ہے',
        dlPng: 'PNG ڈاؤن لوڈ کریں (اعلیٰ معیار)',
        dlPngBusy: 'PNG محفوظ ہو رہا ہے…',
        dlPdf: 'PDF ڈاؤن لوڈ کریں',
        dlPdfBusy: 'PDF تیار ہو رہا ہے…',
        share: 'شیئر کریں',
        shareWa: 'واٹس ایپ پر شیئر کریں',
        mobileSave: 'فون میں محفوظ کریں',
        mobileWa: 'واٹس ایپ',
        close: 'پوسٹر ویور بند کریں',
        attribution: 'مواد براہِ راست حوالہ شدہ علمی کاموں سے اقتباس کیا گیا ہے۔ Amanah ماخذ کے مواد میں ترمیم یا دوبارہ تشریح نہیں کرتا — پوسٹرز تعلیم اور کمیونٹی میں شیئرنگ کے لیے پیش کیے گئے ہیں۔',
      },
  },

  fr: {
      hero: {
        ctaSuccession: 'Planification successorale',
      },
      quickCalc: {
        title: 'Vérification rapide de la Zakat',
        cash: 'Liquidités et épargne',
        debts: 'Dettes à court terme',
        gold: 'Or (24K)',
        silver: 'Argent (999)',
        nisabBasis: 'Base du Nisab',
        nisabSilver: 'Argent (595g)',
        nisabGold: 'Or (85g)',
        status: 'Statut de la Zakat',
        dueAt: 'Zakat due à {rate}%',
        belowNisab: 'Inférieur au Nisab — aucune Zakat due',
        nisabLabel: 'Nisab',
        netZakatable: 'Net zakatable',
        assets: 'Actifs',
        debtsShort: 'Dettes',
        privacy: 'Reste sur votre appareil. Aucun compte.',
        openFull: 'Voir la répartition complète',
        live24k: '24K en direct',
      },
      sidebar: {
        distributable: 'Patrimoine distribuable',
        gross: 'Brut',
        deductions: 'Déductions',
        progress: 'Progression',
        heirShares: 'Parts des héritiers',
        stepEstate: 'Patrimoine',
        stepDeductions: 'Déductions',
        stepHeirs: 'Héritiers',
        stepResult: 'Résultat',
        jumpToResult: 'Aller au résultat',
        downloadCert: 'Télécharger le certificat',
        enterFirst: 'Saisissez le patrimoine et les héritiers pour voir les parts.',
        pickHeirs: 'Sélectionnez les héritiers à l\'étape 3 pour prévisualiser les parts.',
        remainder: 'Reste',
        privacy: 'Tout ce que vous saisissez à gauche met à jour ce panneau instantanément. Rien ne quitte votre appareil.',
      },
      posters: {
        kicker: 'Affiches éducatives',
        title: 'La sagesse islamique de la richesse, une affiche à la fois.',
        subtitle: 'Enseignements concis et cités par des érudits sur la Zakah, l\'héritage et la richesse éthique — gratuits à télécharger et à partager sur WhatsApp, dans votre mosquée ou avec votre famille.',
        readFull: 'Lire l\'affiche complète',
        comingSoon: 'Prochainement',
        dlPng: 'Télécharger PNG (haute résolution)',
        dlPngBusy: 'Enregistrement PNG…',
        dlPdf: 'Télécharger PDF',
        dlPdfBusy: 'Création du PDF…',
        share: 'Partager',
        shareWa: 'Partager sur WhatsApp',
        mobileSave: 'Enregistrer sur le téléphone',
        mobileWa: 'WhatsApp',
        close: 'Fermer l\'afficheur d\'affiches',
        attribution: 'Le contenu est extrait directement des œuvres érudites citées. Amanah ne modifie ni ne réinterprète le matériel source — les affiches sont présentées à des fins éducatives et de partage communautaire.',
      },
  },

  ms: {
      hero: {
        ctaSuccession: 'Perancangan Penggantian',
      },
      quickCalc: {
        title: 'Semakan pantas Zakat',
        cash: 'Wang tunai & simpanan',
        debts: 'Hutang jangka pendek',
        gold: 'Emas (24K)',
        silver: 'Perak (999)',
        nisabBasis: 'Asas Nisab',
        nisabSilver: 'Perak (595g)',
        nisabGold: 'Emas (85g)',
        status: 'Status Zakat',
        dueAt: 'Zakat perlu dibayar pada {rate}%',
        belowNisab: 'Di bawah Nisab — tiada Zakat perlu dibayar',
        nisabLabel: 'Nisab',
        netZakatable: 'Bersih yang wajib dizakatkan',
        assets: 'Aset',
        debtsShort: 'Hutang',
        privacy: 'Kekal pada peranti anda. Tiada akaun.',
        openFull: 'Lihat pecahan penuh',
        live24k: 'Langsung 24K',
      },
      sidebar: {
        distributable: 'Harta pusaka boleh diagih',
        gross: 'Kasar',
        deductions: 'Potongan',
        progress: 'Kemajuan',
        heirShares: 'Bahagian waris',
        stepEstate: 'Harta Pusaka',
        stepDeductions: 'Potongan',
        stepHeirs: 'Waris',
        stepResult: 'Keputusan',
        jumpToResult: 'Lompat ke keputusan',
        downloadCert: 'Muat turun sijil',
        enterFirst: 'Masukkan harta pusaka + waris untuk melihat bahagian.',
        pickHeirs: 'Pilih waris dalam Langkah 3 untuk pratonton bahagian.',
        remainder: 'Baki',
        privacy: 'Semua yang anda taip di sebelah kiri mengemas kini panel ini serta-merta. Tiada maklumat keluar dari peranti anda.',
      },
      posters: {
        kicker: 'Poster Pengetahuan',
        title: 'Kebijaksanaan harta Islam, satu poster pada satu masa.',
        subtitle: 'Pengajaran ringkas berpandukan ulama mengenai Zakah, pusaka dan harta beretika — percuma untuk dimuat turun dan dikongsi di WhatsApp, di masjid anda, atau dengan keluarga.',
        readFull: 'Baca poster penuh',
        comingSoon: 'Akan datang',
        dlPng: 'Muat turun PNG (resolusi tinggi)',
        dlPngBusy: 'Menyimpan PNG…',
        dlPdf: 'Muat turun PDF',
        dlPdfBusy: 'Membina PDF…',
        share: 'Kongsi',
        shareWa: 'Kongsi di WhatsApp',
        mobileSave: 'Simpan ke telefon',
        mobileWa: 'WhatsApp',
        close: 'Tutup paparan poster',
        attribution: 'Kandungan dipetik terus daripada karya ilmiah yang disebut. Amanah tidak mengubah atau mentafsir semula bahan sumber — poster dibentangkan untuk pendidikan dan perkongsian komuniti.',
      },
  },

  tr: {
      hero: {
        ctaSuccession: 'Veraset Planlaması',
      },
      quickCalc: {
        title: 'Hızlı Zakat kontrolü',
        cash: 'Nakit ve tasarruflar',
        debts: 'Kısa vadeli borçlar',
        gold: 'Altın (24K)',
        silver: 'Gümüş (999)',
        nisabBasis: 'Nisab esası',
        nisabSilver: 'Gümüş (595g)',
        nisabGold: 'Altın (85g)',
        status: 'Zakat durumu',
        dueAt: '{rate}% oranında ödenecek Zakat',
        belowNisab: 'Nisab altında — Zakat borcu yoktur',
        nisabLabel: 'Nisab',
        netZakatable: 'Net zekata tabi',
        assets: 'Varlıklar',
        debtsShort: 'Borçlar',
        privacy: 'Cihazınızda kalır. Hesap gerektirmez.',
        openFull: 'Tam dökümü görüntüle',
        live24k: 'Anlık 24K',
      },
      sidebar: {
        distributable: 'Dağıtılabilir tereke',
        gross: 'Brüt',
        deductions: 'Kesintiler',
        progress: 'İlerleme',
        heirShares: 'Varis payları',
        stepEstate: 'Tereke',
        stepDeductions: 'Kesintiler',
        stepHeirs: 'Varisler',
        stepResult: 'Sonuç',
        jumpToResult: 'Sonuca git',
        downloadCert: 'Sertifika indir',
        enterFirst: 'Payları görmek için tereke ve varisler giriniz.',
        pickHeirs: 'Payları önizlemek için Adım 3\'te varisler seçiniz.',
        remainder: 'Kalan',
        privacy: 'Soldaki her girdiniz bu paneli anında günceller. Hiçbir veri cihazınızdan çıkmaz.',
      },
      posters: {
        kicker: 'Bilgi Posterleri',
        title: 'İslami servet bilgeliği, her seferinde bir poster.',
        subtitle: 'Zakah, miras ve etik servet hakkında alim kaynaklı, özet öğretiler — WhatsApp\'ta, mescidinizde veya ailenizle paylaşmak üzere ücretsiz indirin.',
        readFull: 'Tam posteri oku',
        comingSoon: 'Yakında',
        dlPng: 'PNG indir (yüksek çözünürlük)',
        dlPngBusy: 'PNG kaydediliyor…',
        dlPdf: 'PDF indir',
        dlPdfBusy: 'PDF oluşturuluyor…',
        share: 'Paylaş',
        shareWa: 'WhatsApp\'ta paylaş',
        mobileSave: 'Telefona kaydet',
        mobileWa: 'WhatsApp',
        close: 'Poster görüntüleyiciyi kapat',
        attribution: 'İçerik doğrudan alıntılanan ilmi eserlerden alınmıştır. Amanah kaynak materyali değiştirmez veya yeniden yorumlamaz — posterler eğitim ve topluluk paylaşımı için sunulmuştur.',
      },
  },

  ru: {
      hero: {
        ctaSuccession: 'Планирование наследства',
      },
      quickCalc: {
        title: 'Быстрая проверка Zakat',
        cash: 'Наличные и сбережения',
        debts: 'Краткосрочные долги',
        gold: 'Золото (24K)',
        silver: 'Серебро (999)',
        nisabBasis: 'Основа Nisab',
        nisabSilver: 'Серебро (595г)',
        nisabGold: 'Золото (85г)',
        status: 'Статус Zakat',
        dueAt: 'Zakat к уплате {rate}%',
        belowNisab: 'Ниже Nisab — Zakat не требуется',
        nisabLabel: 'Nisab',
        netZakatable: 'Чистая облагаемая база',
        assets: 'Активы',
        debtsShort: 'Долги',
        privacy: 'Остаётся на вашем устройстве. Без учётной записи.',
        openFull: 'Посмотреть полную разбивку',
        live24k: 'Актуальная цена 24K',
      },
      sidebar: {
        distributable: 'Распределяемое наследство',
        gross: 'Валовое',
        deductions: 'Вычеты',
        progress: 'Прогресс',
        heirShares: 'Доли наследников',
        stepEstate: 'Наследство',
        stepDeductions: 'Вычеты',
        stepHeirs: 'Наследники',
        stepResult: 'Результат',
        jumpToResult: 'Перейти к результату',
        downloadCert: 'Скачать сертификат',
        enterFirst: 'Введите наследство и наследников для просмотра долей.',
        pickHeirs: 'Выберите наследников в Шаге 3 для предпросмотра долей.',
        remainder: 'Остаток',
        privacy: 'Всё, что вы вводите слева, мгновенно обновляет эту панель. Ничего не покидает ваше устройство.',
      },
      posters: {
        kicker: 'Образовательные постеры',
        title: 'Исламская мудрость о богатстве, по одному постеру за раз.',
        subtitle: 'Краткие учения со ссылками на учёных о Zakah, наследовании и этичном богатстве — бесплатно для загрузки и распространения в WhatsApp, в вашей мечети или среди семьи.',
        readFull: 'Прочитать полный постер',
        comingSoon: 'Скоро',
        dlPng: 'Скачать PNG (высокое разрешение)',
        dlPngBusy: 'Сохранение PNG…',
        dlPdf: 'Скачать PDF',
        dlPdfBusy: 'Создание PDF…',
        share: 'Поделиться',
        shareWa: 'Поделиться в WhatsApp',
        mobileSave: 'Сохранить в телефон',
        mobileWa: 'WhatsApp',
        close: 'Закрыть просмотр постера',
        attribution: 'Содержание взято непосредственно из цитируемых научных работ. Amanah не изменяет и не интерпретирует исходный материал — постеры представлены для образования и общественного обмена.',
      },
  },

  hi: {
      hero: {
        ctaSuccession: 'उत्तराधिकार योजना',
      },
      quickCalc: {
        title: 'त्वरित Zakat जाँच',
        cash: 'नकद और बचत',
        debts: 'अल्पकालिक ऋण',
        gold: 'सोना (24K)',
        silver: 'चांदी (999)',
        nisabBasis: 'Nisab आधार',
        nisabSilver: 'चांदी (595g)',
        nisabGold: 'सोना (85g)',
        status: 'Zakat स्थिति',
        dueAt: 'Zakat देय {rate}% पर',
        belowNisab: 'Nisab से नीचे — कोई Zakat देय नहीं',
        nisabLabel: 'Nisab',
        netZakatable: 'शुद्ध zakatable',
        assets: 'परिसंपत्तियाँ',
        debtsShort: 'ऋण',
        privacy: 'आपके डिवाइस पर रहता है। कोई खाता नहीं।',
        openFull: 'पूर्ण विवरण देखें',
        live24k: 'लाइव 24K',
      },
      sidebar: {
        distributable: 'वितरणीय संपत्ति',
        gross: 'सकल',
        deductions: 'कटौतियाँ',
        progress: 'प्रगति',
        heirShares: 'उत्तराधिकारी हिस्से',
        stepEstate: 'संपत्ति',
        stepDeductions: 'कटौतियाँ',
        stepHeirs: 'उत्तराधिकारी',
        stepResult: 'परिणाम',
        jumpToResult: 'परिणाम पर जाएं',
        downloadCert: 'प्रमाणपत्र डाउनलोड करें',
        enterFirst: 'हिस्से देखने के लिए संपत्ति + उत्तराधिकारी दर्ज करें।',
        pickHeirs: 'हिस्सों का पूर्वावलोकन करने के लिए चरण 3 में उत्तराधिकारी चुनें।',
        remainder: 'शेष',
        privacy: 'बाईं ओर आपके द्वारा टाइप की गई हर चीज़ इस पैनल को तुरंत अपडेट करती है। कुछ भी आपके डिवाइस से बाहर नहीं जाता।',
      },
      posters: {
        kicker: 'ज्ञान पोस्टर',
        title: 'इस्लामी धन ज्ञान, एक बार में एक पोस्टर।',
        subtitle: 'Zakah, विरासत और नैतिक धन पर विद्वान-उद्धृत शिक्षाएँ — WhatsApp पर, आपकी मस्जिद में, या परिवार के साथ डाउनलोड और साझा करने के लिए निःशुल्क।',
        readFull: 'पूर्ण पोस्टर पढ़ें',
        comingSoon: 'जल्द आ रहा है',
        dlPng: 'PNG डाउनलोड करें (उच्च-रिज़ॉल्यूशन)',
        dlPngBusy: 'PNG सहेजा जा रहा है…',
        dlPdf: 'PDF डाउनलोड करें',
        dlPdfBusy: 'PDF बनाया जा रहा है…',
        share: 'साझा करें',
        shareWa: 'WhatsApp पर साझा करें',
        mobileSave: 'फ़ोन में सहेजें',
        mobileWa: 'WhatsApp',
        close: 'पोस्टर व्यूअर बंद करें',
        attribution: 'सामग्री सीधे उद्धृत विद्वतापूर्ण कार्यों से उद्धृत है। Amanah स्रोत सामग्री को संशोधित या पुनर्व्याख्यायित नहीं करता है — पोस्टर शिक्षा और सामुदायिक साझाकरण के लिए प्रस्तुत किए गए हैं।',
      },
  },

  bn: {
      hero: {
        ctaSuccession: 'উত্তরাধিকার পরিকল্পনা',
      },
      quickCalc: {
        title: 'দ্রুত যাকাত পরীক্ষা',
        cash: 'নগদ ও সঞ্চয়',
        debts: 'স্বল্পমেয়াদী ঋণ',
        gold: 'সোনা (২৪ ক্যারেট)',
        silver: 'রূপা (৯৯৯)',
        nisabBasis: 'নিসাব ভিত্তি',
        nisabSilver: 'রূপা (৫৯৫ গ্রাম)',
        nisabGold: 'সোনা (৮৫ গ্রাম)',
        status: 'যাকাতের অবস্থা',
        dueAt: '{rate}% হারে যাকাত প্রদেয়',
        belowNisab: 'নিসাবের নিচে — কোনো যাকাত প্রদেয় নয়',
        nisabLabel: 'নিসাব',
        netZakatable: 'নিট যাকাতযোগ্য',
        assets: 'সম্পদ',
        debtsShort: 'ঋণ',
        privacy: 'আপনার ডিভাইসে থাকে। কোনো অ্যাকাউন্ট নেই।',
        openFull: 'সম্পূর্ণ বিবরণ দেখুন',
        live24k: 'লাইভ ২৪ ক্যারেট',
      },
      sidebar: {
        distributable: 'বণ্টনযোগ্য সম্পত্তি',
        gross: 'মোট',
        deductions: 'বাদ',
        progress: 'অগ্রগতি',
        heirShares: 'উত্তরাধিকারীদের অংশ',
        stepEstate: 'সম্পত্তি',
        stepDeductions: 'বাদ',
        stepHeirs: 'উত্তরাধিকারী',
        stepResult: 'ফলাফল',
        jumpToResult: 'ফলাফলে যান',
        downloadCert: 'সার্টিফিকেট ডাউনলোড করুন',
        enterFirst: 'অংশ দেখতে সম্পত্তি + উত্তরাধিকারী লিখুন।',
        pickHeirs: 'অংশের পূর্বরূপ দেখতে ধাপ ৩-এ উত্তরাধিকারী নির্বাচন করুন।',
        remainder: 'অবশিষ্ট',
        privacy: 'বামে যা টাইপ করেন তা তাৎক্ষণিকভাবে এই প্যানেল আপডেট করে। কিছুই আপনার ডিভাইস ছাড়ে না।',
      },
      posters: {
        kicker: 'জ্ঞান পোস্টার',
        title: 'ইসলামী সম্পদ জ্ঞান, একবারে একটি পোস্টার।',
        subtitle: 'যাকাত, উত্তরাধিকার এবং নৈতিক সম্পদ সম্পর্কে সংক্ষিপ্ত, আলেম-উদ্ধৃত শিক্ষা — হোয়াটসঅ্যাপে, আপনার মসজিদে বা পরিবারের সাথে ডাউনলোড ও শেয়ার করতে বিনামূল্যে।',
        readFull: 'সম্পূর্ণ পোস্টার পড়ুন',
        comingSoon: 'শীঘ্রই আসছে',
        dlPng: 'PNG ডাউনলোড করুন (উচ্চ-রেজোলিউশন)',
        dlPngBusy: 'PNG সংরক্ষণ করা হচ্ছে…',
        dlPdf: 'PDF ডাউনলোড করুন',
        dlPdfBusy: 'PDF তৈরি হচ্ছে…',
        share: 'শেয়ার করুন',
        shareWa: 'হোয়াটসঅ্যাপে শেয়ার করুন',
        mobileSave: 'ফোনে সংরক্ষণ করুন',
        mobileWa: 'হোয়াটসঅ্যাপ',
        close: 'পোস্টার ভিউয়ার বন্ধ করুন',
        attribution: 'বিষয়বস্তু সরাসরি উদ্ধৃত পাণ্ডিত্যপূর্ণ কাজ থেকে নেওয়া হয়েছে। আমানাহ উৎস উপাদান পরিবর্তন বা পুনর্ব্যাখ্যা করে না — পোস্টারগুলি শিক্ষা এবং সম্প্রদায় ভাগাভাগির জন্য উপস্থাপন করা হয়।',
      },
  },

  ml: {
      hero: {
        ctaSuccession: 'പിന്തുടർച്ചാവകാശ ആസൂത്രണം',
      },
      quickCalc: {
        title: 'ദ്രുത Zakat പരിശോധന',
        cash: 'നാണയവും സമ്പാദ്യവും',
        debts: 'ഹ്രസ്വകാല കടങ്ങൾ',
        gold: 'സ്വർണ്ണം (24K)',
        silver: 'വെള്ളി (999)',
        nisabBasis: 'Nisab അടിസ്ഥാനം',
        nisabSilver: 'വെള്ളി (595g)',
        nisabGold: 'സ്വർണ്ണം (85g)',
        status: 'Zakat നില',
        dueAt: 'Zakat അടയ്‌ക്കേണ്ടത് {rate}%',
        belowNisab: 'Nisab-ന് താഴെ — Zakat ബാധ്യതയില്ല',
        nisabLabel: 'Nisab',
        netZakatable: 'നെറ്റ് zakatable',
        assets: 'ആസ്തികൾ',
        debtsShort: 'കടങ്ങൾ',
        privacy: 'നിങ്ങളുടെ ഉപകരണത്തിൽ തന്നെ. അക്കൗണ്ട് ആവശ്യമില്ല.',
        openFull: 'പൂർണ്ണ വിശദാംശങ്ങൾ കാണുക',
        live24k: 'തത്സമയ 24K',
      },
      sidebar: {
        distributable: 'വിതരണയോഗ്യമായ സ്വത്ത്',
        gross: 'മൊത്തം',
        deductions: 'കിഴിവുകൾ',
        progress: 'പുരോഗതി',
        heirShares: 'അനന്തരാവകാശികളുടെ ഓഹരികൾ',
        stepEstate: 'സ്വത്ത്',
        stepDeductions: 'കിഴിവുകൾ',
        stepHeirs: 'അനന്തരാവകാശികൾ',
        stepResult: 'ഫലം',
        jumpToResult: 'ഫലത്തിലേക്ക് പോകുക',
        downloadCert: 'സർട്ടിഫിക്കറ്റ് ഡൗൺലോഡ് ചെയ്യുക',
        enterFirst: 'ഓഹരികൾ കാണാൻ സ്വത്ത് + അനന്തരാവകാശികൾ നൽകുക.',
        pickHeirs: 'ഓഹരികളുടെ പ്രിവ്യൂ കാണാൻ ഘട്ടം 3-ൽ അനന്തരാവകാശികളെ തിരഞ്ഞെടുക്കുക.',
        remainder: 'ശേഷിപ്പ്',
        privacy: 'ഇടതുവശത്ത് നിങ്ങൾ ടൈപ്പ് ചെയ്യുന്നതെല്ലാം ഈ പാനൽ ഉടൻ അപ്ഡേറ്റ് ചെയ്യുന്നു. ഒന്നും നിങ്ങളുടെ ഉപകരണം വിട്ടുപോകുന്നില്ല.',
      },
      posters: {
        kicker: 'അറിവ് പോസ്റ്ററുകൾ',
        title: 'ഇസ്‌ലാമിക സമ്പത്ത് ജ്ഞാനം, ഓരോ പോസ്റ്ററിലും.',
        subtitle: 'Zakah, പിന്തുടർച്ചാവകാശം, ധാർമിക സമ്പത്ത് എന്നിവയെക്കുറിച്ചുള്ള ചെറിയ, പണ്ഡിതന്മാർ ഉദ്ധരിച്ച പഠിപ്പിക്കലുകൾ — വാട്ട്‌സ്ആപ്പിൽ, നിങ്ങളുടെ മസ്ജിദിൽ അല്ലെങ്കിൽ കുടുംബത്തോടൊപ്പം ഡൗൺലോഡ് ചെയ്യാനും പങ്കിടാനും സൗജന്യം.',
        readFull: 'പൂർണ്ണ പോസ്റ്റർ വായിക്കുക',
        comingSoon: 'ഉടൻ വരുന്നു',
        dlPng: 'PNG ഡൗൺലോഡ് ചെയ്യുക (ഉയർന്ന റെസല്യൂഷൻ)',
        dlPngBusy: 'PNG സംരക്ഷിക്കുന്നു…',
        dlPdf: 'PDF ഡൗൺലോഡ് ചെയ്യുക',
        dlPdfBusy: 'PDF നിർമ്മിക്കുന്നു…',
        share: 'പങ്കിടുക',
        shareWa: 'വാട്ട്‌സ്ആപ്പിൽ പങ്കിടുക',
        mobileSave: 'ഫോണിൽ സംരക്ഷിക്കുക',
        mobileWa: 'വാട്ട്‌സ്ആപ്പ്',
        close: 'പോസ്റ്റർ വ്യൂവർ അടയ്ക്കുക',
        attribution: 'ഉദ്ധരിച്ച പണ്ഡിത കൃതികളിൽ നിന്ന് നേരിട്ട് ഉള്ളടക്കം എടുത്തിരിക്കുന്നു. Amanah ഉറവിട വസ്തുക്കൾ പരിഷ്‌ക്കരിക്കുകയോ വീണ്ടും വ്യാഖ്യാനിക്കുകയോ ചെയ്യുന്നില്ല — പോസ്റ്ററുകൾ വിദ്യാഭ്യാസത്തിനും സമൂഹ പങ്കിടലിനും വേണ്ടി അവതരിപ്പിച്ചിരിക്കുന്നു.',
      },
  },

  id: {
      hero: {
        ctaSuccession: 'Perencanaan Suksesi',
      },
      quickCalc: {
        title: 'Pengecekan Zakat Cepat',
        cash: 'Kas & tabungan',
        debts: 'Utang jangka pendek',
        gold: 'Emas (24K)',
        silver: 'Perak (999)',
        nisabBasis: 'Dasar Nisab',
        nisabSilver: 'Perak (595g)',
        nisabGold: 'Emas (85g)',
        status: 'Status Zakat',
        dueAt: 'Zakat yang harus dibayar sebesar {rate}%',
        belowNisab: 'Di bawah Nisab — tidak ada Zakat yang wajib dibayar',
        nisabLabel: 'Nisab',
        netZakatable: 'Harta yang wajib dizakati (bersih)',
        assets: 'Aset',
        debtsShort: 'Utang',
        privacy: 'Tersimpan di perangkat Anda. Tanpa akun.',
        openFull: 'Lihat rincian lengkap',
        live24k: 'Langsung 24K',
      },
      sidebar: {
        distributable: 'Harta warisan yang dapat dibagikan',
        gross: 'Bruto',
        deductions: 'Pengurangan',
        progress: 'Progres',
        heirShares: 'Bagian ahli waris',
        stepEstate: 'Harta Warisan',
        stepDeductions: 'Pengurangan',
        stepHeirs: 'Ahli Waris',
        stepResult: 'Hasil',
        jumpToResult: 'Lompat ke hasil',
        downloadCert: 'Unduh sertifikat',
        enterFirst: 'Masukkan harta warisan + ahli waris untuk melihat bagian.',
        pickHeirs: 'Pilih ahli waris di Langkah 3 untuk melihat pratinjau bagian.',
        remainder: 'Sisa',
        privacy: 'Semua yang Anda ketik di sebelah kiri memperbarui panel ini secara instan. Tidak ada yang keluar dari perangkat Anda.',
      },
      posters: {
        kicker: 'Poster Pengetahuan',
        title: 'Kebijaksanaan kekayaan Islami, satu poster dalam satu waktu.',
        subtitle: 'Ajaran ringkas dengan rujukan ulama tentang Zakah, warisan, dan kekayaan yang etis — gratis untuk diunduh dan dibagikan di WhatsApp, di masjid Anda, atau dengan keluarga.',
        readFull: 'Baca poster lengkap',
        comingSoon: 'Segera hadir',
        dlPng: 'Unduh PNG (resolusi tinggi)',
        dlPngBusy: 'Menyimpan PNG…',
        dlPdf: 'Unduh PDF',
        dlPdfBusy: 'Membuat PDF…',
        share: 'Bagikan',
        shareWa: 'Bagikan di WhatsApp',
        mobileSave: 'Simpan ke ponsel',
        mobileWa: 'WhatsApp',
        close: 'Tutup tampilan poster',
        attribution: 'Konten dikutip langsung dari karya ilmiah yang dirujuk. Amanah tidak mengubah atau menafsirkan ulang materi sumber — poster disajikan untuk pendidikan dan berbagi komunitas.',
      },
  },

  fa: {
      hero: {
        ctaSuccession: 'برنامه‌ریزی جانشینی',
      },
      quickCalc: {
        title: 'بررسی سریع Zakat',
        cash: 'پول نقد و پس‌انداز',
        debts: 'بدهی‌های کوتاه‌مدت',
        gold: 'طلا (۲۴ عیار)',
        silver: 'نقره (۹۹۹)',
        nisabBasis: 'مبنای Nisab',
        nisabSilver: 'نقره (۵۹۵ گرم)',
        nisabGold: 'طلا (۸۵ گرم)',
        status: 'وضعیت Zakat',
        dueAt: 'Zakat واجب به میزان {rate}٪',
        belowNisab: 'زیر Nisab — Zakat واجب نیست',
        nisabLabel: 'Nisab',
        netZakatable: 'خالص مشمول Zakah',
        assets: 'دارایی‌ها',
        debtsShort: 'بدهی‌ها',
        privacy: 'روی دستگاه شما باقی می‌ماند. بدون حساب کاربری.',
        openFull: 'مشاهده جزئیات کامل',
        live24k: 'زنده ۲۴ عیار',
      },
      sidebar: {
        distributable: 'ترکه قابل تقسیم',
        gross: 'ناخالص',
        deductions: 'کسورات',
        progress: 'پیشرفت',
        heirShares: 'سهم وراث',
        stepEstate: 'ترکه',
        stepDeductions: 'کسورات',
        stepHeirs: 'وراث',
        stepResult: 'نتیجه',
        jumpToResult: 'رفتن به نتیجه',
        downloadCert: 'دانلود گواهی',
        enterFirst: 'ابتدا ترکه و وراث را وارد کنید تا سهم‌ها نمایش داده شود.',
        pickHeirs: 'در مرحله ۳ وراث را انتخاب کنید تا پیش‌نمایش سهم‌ها نمایش داده شود.',
        remainder: 'باقیمانده',
        privacy: 'هر آنچه در سمت چپ تایپ می‌کنید، بلافاصله این پنل را به‌روزرسانی می‌کند. هیچ‌چیز از دستگاه شما خارج نمی‌شود.',
      },
      posters: {
        kicker: 'پوسترهای دانش',
        title: 'حکمت ثروت اسلامی، یک پوستر در هر زمان.',
        subtitle: 'آموزه‌های خلاصه و مستند به منابع عالمان درباره Zakah، میراث و ثروت اخلاقی — رایگان برای دانلود و اشتراک‌گذاری در واتساپ، در مسجد یا با خانواده.',
        readFull: 'خواندن پوستر کامل',
        comingSoon: 'به زودی',
        dlPng: 'دانلود PNG (با کیفیت بالا)',
        dlPngBusy: 'در حال ذخیره PNG…',
        dlPdf: 'دانلود PDF',
        dlPdfBusy: 'در حال ساخت PDF…',
        share: 'اشتراک‌گذاری',
        shareWa: 'اشتراک‌گذاری در واتساپ',
        mobileSave: 'ذخیره در تلفن',
        mobileWa: 'واتساپ',
        close: 'بستن نمایشگر پوستر',
        attribution: 'محتوا مستقیماً از آثار علمی ذکر شده استخراج شده است. Amanah محتوا را تغییر یا تفسیر مجدد نمی‌کند — پوسترها برای آموزش و اشتراک‌گذاری جامعه ارائه می‌شوند.',
      },
  },

  prs: {
      hero: {
        ctaSuccession: 'برنامه‌ریزی جانشینی',
      },
      quickCalc: {
        title: 'محاسبهٔ سریع زکات',
        cash: 'پول نقد و پس‌انداز',
        debts: 'قروض کوتاه‌مدت',
        gold: 'طلا (۲۴ عیار)',
        silver: 'نقره (۹۹۹)',
        nisabBasis: 'اساس Nisab',
        nisabSilver: 'نقره (۵۹۵ گرام)',
        nisabGold: 'طلا (۸۵ گرام)',
        status: 'وضعیت Zakat',
        dueAt: 'Zakat واجب به میزان {rate}٪',
        belowNisab: 'زیر Nisab — هیچ Zakat واجب نیست',
        nisabLabel: 'Nisab',
        netZakatable: 'خالص قابل Zakah',
        assets: 'دارایی‌ها',
        debtsShort: 'قروض',
        privacy: 'در دستگاه شما باقی می‌ماند. بدون حساب کاربری.',
        openFull: 'مشاهدهٔ جزئیات کامل',
        live24k: 'قیمت لحظه‌ای ۲۴ عیار',
      },
      sidebar: {
        distributable: 'ترکهٔ قابل توزیع',
        gross: 'ناخالص',
        deductions: 'کسورات',
        progress: 'پیشرفت',
        heirShares: 'سهم وارثان',
        stepEstate: 'ترکه',
        stepDeductions: 'کسورات',
        stepHeirs: 'وارثان',
        stepResult: 'نتیجه',
        jumpToResult: 'پرش به نتیجه',
        downloadCert: 'دانلود گواهی',
        enterFirst: 'ترکه و وارثان را وارد کنید تا سهم‌ها را ببینید.',
        pickHeirs: 'در مرحلهٔ ۳ وارثان را انتخاب کنید تا پیش‌نمایش سهم‌ها را ببینید.',
        remainder: 'باقیمانده',
        privacy: 'هرچیزی که در سمت چپ تایپ می‌کنید فوراً این پنل را به‌روز می‌کند. هیچ‌چیز از دستگاه شما خارج نمی‌شود.',
      },
      posters: {
        kicker: 'پوسترهای دانشی',
        title: 'حکمت ثروت اسلامی، یک پوستر در یک زمان.',
        subtitle: 'تعلیمات کوچک و مستند از علما دربارهٔ Zakah، میراث و ثروت اخلاقی — رایگان برای دانلود و اشتراک در واتس‌اپ، در مسجد، یا با خانواده.',
        readFull: 'خواندن پوستر کامل',
        comingSoon: 'به زودی',
        dlPng: 'دانلود PNG (رزولوشن بالا)',
        dlPngBusy: 'در حال ذخیرهٔ PNG…',
        dlPdf: 'دانلود PDF',
        dlPdfBusy: 'در حال ساخت PDF…',
        share: 'اشتراک‌گذاری',
        shareWa: 'اشتراک در واتس‌اپ',
        mobileSave: 'ذخیره در تلفن',
        mobileWa: 'واتس‌اپ',
        close: 'بستن نمایشگر پوستر',
        attribution: 'محتوا مستقیماً از آثار علمی ذکرشده استخراج شده است. Amanah محتوای منبع را تغییر یا تفسیر مجدد نمی‌دهد — پوسترها برای آموزش و اشتراک‌گذاری جامعه ارائه می‌شوند.',
      },
  },

  sw: {
      hero: {
        ctaSuccession: 'Mipango ya Urithi',
      },
      quickCalc: {
        title: 'Ukaguzi wa haraka wa Zakat',
        cash: 'Fedha taslimu na akiba',
        debts: 'Madeni ya muda mfupi',
        gold: 'Dhahabu (24K)',
        silver: 'Fedha (999)',
        nisabBasis: 'Msingi wa Nisab',
        nisabSilver: 'Fedha (595g)',
        nisabGold: 'Dhahabu (85g)',
        status: 'Hali ya Zakat',
        dueAt: 'Zakat inayostahili kwa {rate}%',
        belowNisab: 'Chini ya Nisab — hakuna Zakat inayodaiwa',
        nisabLabel: 'Nisab',
        netZakatable: 'Safi inayotosha kwa Zakat',
        assets: 'Mali',
        debtsShort: 'Madeni',
        privacy: 'Inabaki kwenye kifaa chako. Hakuna akaunti.',
        openFull: 'Angalia maelezo kamili',
        live24k: '24K ya moja kwa moja',
      },
      sidebar: {
        distributable: 'Mali inayogawanyika',
        gross: 'Jumla',
        deductions: 'Mapunguzo',
        progress: 'Maendeleo',
        heirShares: 'Hisa za warithi',
        stepEstate: 'Mali',
        stepDeductions: 'Mapunguzo',
        stepHeirs: 'Warithi',
        stepResult: 'Matokeo',
        jumpToResult: 'Nenda moja kwa moja kwa matokeo',
        downloadCert: 'Pakua cheti',
        enterFirst: 'Ingiza mali + warithi ili kuona hisa.',
        pickHeirs: 'Chagua warithi katika Hatua ya 3 ili kuonyesha mapitio ya hisa.',
        remainder: 'Iliyobaki',
        privacy: 'Kila kitu unachoandika upande wa kushoto kinasasisha paneli hii mara moja. Hakuna kinachotoka kwenye kifaa chako.',
      },
      posters: {
        kicker: 'Mabango ya Elimu',
        title: 'Hekima ya mali ya Kiislamu, bango moja kwa wakati.',
        subtitle: 'Mafundisho mafupi, yenye marejeleo ya wanavyuoni kuhusu Zakah, urithi na mali halali — bure kupakua na kushiriki kwenye WhatsApp, msikitini mwako, au na familia.',
        readFull: 'Soma bango kamili',
        comingSoon: 'Inakuja hivi karibuni',
        dlPng: 'Pakua PNG (ubora wa juu)',
        dlPngBusy: 'Inahifadhi PNG…',
        dlPdf: 'Pakua PDF',
        dlPdfBusy: 'Inaunda PDF…',
        share: 'Shiriki',
        shareWa: 'Shiriki kwenye WhatsApp',
        mobileSave: 'Hifadhi kwenye simu',
        mobileWa: 'WhatsApp',
        close: 'Funga kionyeshaji cha bango',
        attribution: 'Maudhui yametolewa moja kwa moja kutoka kwa kazi za kinadharia zilizotajwa. Amanah haibadilishi wala kuipiga tafsiri upya nyenzo asili — mabango yanawasilishwa kwa ajili ya elimu na kushiriki na jamii.',
      },
  },

  uz: {
      hero: {
        ctaSuccession: 'Merosxo\'rlik rejalashtirish',
      },
      quickCalc: {
        title: 'Tez Zakat tekshiruvi',
        cash: 'Naqd pul va jamg\'armalar',
        debts: 'Qisqa muddatli qarzlar',
        gold: 'Oltin (24K)',
        silver: 'Kumush (999)',
        nisabBasis: 'Nisab asosi',
        nisabSilver: 'Kumush (595g)',
        nisabGold: 'Oltin (85g)',
        status: 'Zakat holati',
        dueAt: 'Zakat {rate}% miqdorida to\'lanishi kerak',
        belowNisab: 'Nisab ostida — Zakat to\'lanmaydi',
        nisabLabel: 'Nisab',
        netZakatable: 'Sof zakatlashtirilishi kerak bo\'lgan',
        assets: 'Aktivlar',
        debtsShort: 'Qarzlar',
        privacy: 'Qurilmangizda qoladi. Hisob emas.',
        openFull: 'To\'liq taqsimotni ko\'rish',
        live24k: 'Jonli 24K',
      },
      sidebar: {
        distributable: 'Taqsimlanishi mumkin bo\'lgan meros',
        gross: 'Umumiy',
        deductions: 'Chegirmalar',
        progress: 'Jarayon',
        heirShares: 'Merosxo\'rlar ulushi',
        stepEstate: 'Meros',
        stepDeductions: 'Chegirmalar',
        stepHeirs: 'Merosxo\'rlar',
        stepResult: 'Natija',
        jumpToResult: 'Natijaga o\'tish',
        downloadCert: 'Sertifikatni yuklab olish',
        enterFirst: 'Ulushlarni ko\'rish uchun meros + merosxo\'rlarni kiriting.',
        pickHeirs: 'Ulushlarni ko\'rish uchun 3-bosqichda merosxo\'rlarni tanlang.',
        remainder: 'Qoldiq',
        privacy: 'Chap tomonda yozgan hamma narsangiz bu panelni zudlik bilan yangilaydi. Hech narsa qurilmangizdan chiqmaydi.',
      },
      posters: {
        kicker: 'Bilim posterlari',
        title: 'Islomiy boylik donishmandligi, bir vaqtning o\'zida bitta poster.',
        subtitle: 'Zakah, meros va axloqiy boylik bo\'yicha alimlar tomonidan keltirilgan qisqa ta\'limotlar — WhatsApp-da, masjidingizda yoki oilangiz bilan ulashish uchun bepul yuklab oling.',
        readFull: 'To\'liq posterni o\'qish',
        comingSoon: 'Tez orada',
        dlPng: 'PNG yuklab olish (yuqori sifatli)',
        dlPngBusy: 'PNG saqlanmoqda…',
        dlPdf: 'PDF yuklab olish',
        dlPdfBusy: 'PDF tayyorlanmoqda…',
        share: 'Ulashish',
        shareWa: 'WhatsApp-da ulashish',
        mobileSave: 'Telefonga saqlash',
        mobileWa: 'WhatsApp',
        close: 'Poster ko\'rinishini yopish',
        attribution: 'Kontent bevosita keltirilgan ilmiy asarlardan olingan. Amanah manba materialini o\'zgartirmaydi yoki qayta sharhlaydi — posterlar ta\'lim va jamoa ulashish uchun taqdim etiladi.',
      },
  },

};

/**
 * tSurf(lang, path) — dot-path lookup with English fallback.
 */
export const tSurf = (lang, path) => {
  const enter = (obj, p) => p.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
  const primary = enter(newSurfacesTranslations[lang], path);
  if (primary != null) return primary;
  const fallback = enter(newSurfacesTranslations.en, path);
  return fallback ?? path;
};
